// tslint:disable:no-shadowed-variable
/**
 * Helpful to make it more readable for which mouse button was pressed in events
 *
 * @example
 * ```ts
 * function click(ev: MouseEvent) {
 *   if (ev.button === MouseButton.Left) ...;
 * }
 * ```
 */
// The PURE directive let's bundlers know `Object.freeze` has no side-effects
const MouseButton = /*@__PURE__*/ Object.freeze({
    /** Main button (usually left mouse) */
    Left: 0,
    /** Auxiliary button (usually mouse wheel) */
    Middle: 1,
    /** Secondary button (usually right mouse) */
    Right: 2,
    /** Fourth button (usually on the side, and bound to "Browser Back") */
    Fourth: 3,
    /** Fifth button (usually on the side, and bound to "Browser Forward") */
    Fifth: 4
});

/**
 * Uses a type predicate to correctly remove `null` and `undefined` from a type,
 * as well as returning `true`/`false` depending on if the value is
 * `null`/`undefined` or not.
 *
 * @see https://www.typescriptlang.org/docs/handbook/advanced-types.html#using-type-predicates
 *
 * @example
 * const defined = isDefined('');
 * // true
 * const defined = isDefined(null);
 * // false
 * const defined = isDefined(undefined);
 * // false
 *
 * @example
 * // Assuming the following method exists
 * declare function numbers(nums: number[]): void;
 *
 * const filtered = [1, undefined, 3].filter(v => v !== undefined);
 * numbers(filtered);
 * //      -------- Error: (number | undefined)[] is not assignable to number[]
 *
 * // This works, but is quite verbose for a something that is relatively common.
 * const filtered = [1, undefined, 3].filter((v): v is Exclude<typeof v, undefined> => v !== undefined);
 * numbers(filtered);
 *
 * // `isDefined()` uses a type guard that ensures the value is not `null` or `undefined`
 * const filtered = [1, undefined, 3].filter(isDefined);
 * numbers(filtered);
 */
function isDefined(value) {
    return value != null;
}

/**
 * Throws assertion error with the optional `message` if the `condition`
 * is not `true`.
 *
 * @param condition If not true, will result in method throwing
 * @param message The message used for the thrown error
 *
 * @example
 * assert(isValid);
 * assert(isBlue, 'Must be blue');
 * assert(color === 'red', 'Color is not red');
 * assert(false); // will throw
 */
function assert(condition, message = 'Assertion error') {
    if (!condition)
        throw new Error(message);
}
/**
 * Throws assertion error with the optional `message` if the `value`
 * is not defined.
 *
 * @param value Must be NonNullable for method not to throw
 * @param message The message used for the thrown error
 *
 * @example
 * assertDefined(name, 'Name must be defined');
 * assertDefined(null); // will throw
 * assertDefined(undefined); // will throw
 */
function assertDefined(value, message) {
    assert(isDefined(value), message);
}
/**
 * Throws assertion error with the optional `message` if the `value`
 * is not a non-null object.
 *
 * @param value Must be a non-null object for method not to throw
 * @param message The message used for the thrown error
 *
 * @example
 * assertObject(options, 'Must provide a options object');
 * assertObject({}); // fine!
 * assertObject(null); // will throw
 */
function assertObject(value, message) {
    assert(isDefined(value) && typeof value === 'object', message);
}
function assertElement(element, tag, message) {
    assertDefined(element, message ?? 'Assertion error: Element must be defined');
    assert(element.localName === tag, message ?? `Assertion error: Element must be "${tag}"`);
}
/**
 * Throws assertion error with the optional `message` if the `url`
 * is not a valid URL.
 *
 * @param value Must be a non-null object for method not to throw
 * @param message The message used for the thrown error
 *
 * @example
 * assertURL('https://example.com'); // fine!
 * assertURL('example.com'); // will throw (missing https://)
 * assertURL({}); // will throw
 */
function assertURL(url, message) {
    const msg = message ?? 'Assertion error: must be a string and valid URL';
    assertString(url, msg);
    try {
        return new URL(url) && undefined;
    }
    catch {
        assert(false, msg);
    }
}
/**
 * Throws assertion error with the optional `message` if the `value`
 * is not a non-empty string.
 *
 * @param value Must be a non-empty string for method not to throw
 * @param message The message used for the thrown error
 * @param suffix Optional suffix the string must end with
 *
 * @example
 * assertString(userName);
 * assertString('foobar', 'Must end with bar', 'bar'); // fine!
 * assertString(''); // will throw
 */
function assertString(value, message, suffix = '') {
    assert(typeof value === 'string' && value.length > 0 && value.endsWith(suffix), message);
}
/**
 * Throws assertion error with the optional `message` if the `value`
 * is not included in the `oneOf` list of items.
 *
 * @param value Must be one of the values in the `oneOf` parameter for method not to throw
 * @param oneOf List of valid values for the `value`
 * @param message The message used for the thrown error
 *
 * @example
 * assertOneOf(sort, sortOptions);
 * assertOneOf(1, [1, 2, 3]); // fine!
 * assertOneOf('d', ['a', 'b', 'c']); // will throw
 */
function assertOneOf(value, oneOf, message) {
    assert(oneOf.some(alternative => value === alternative), message);
}
/**
 * Checks that a value matches a specific kind of type.
 * Useful in tests to verify that type narrowing works as intended.
 *
 * @param _value The value to verify that `Type` matches against
 *
 * @example
 * assertType<boolean>(true);
 * assertType<string>('hello!');
 * assertType<number>({}); // Type error!
 */
function assertType(_value) { }
/**
 * Helps avoids by ensuring that all cases are handled in for instance
 * switch cases. If all cases are handled, the type will become `never`
 * to indicate it should not be anything ever.
 *
 * This method will throw if called to ensure it is not executed
 * at runtime, should the types used be incorrect.
 *
 * @param _value The value that should be of type `never`
 *
 * @example
 * function something(v: 'a' | 'b')
 *   switch (v) {
 *     case 'a':
 *       return 'foo';
 *     default:
 *       // Oops! Can be `'b'`, does not match `never`!
 *       assertUnreachable(v);
 *   }
 * }
 */
function assertUnreachable(_value) {
    throw new Error('Assertion error: Method should not be called.');
}

const PRIMITIVE_TYPES = ['number', 'boolean', 'string'];
/**
 *
 * @internal
 * Ensures type of value is primitive
 */
function isPrimitive(value) {
    return PRIMITIVE_TYPES.includes(typeof value);
}

function cloneMap(target) {
    const result = new Map();
    for (const [key, value] of target.entries()) {
        result.set(key, cloneDeep(value));
    }
    return result;
}
function cloneSet(target) {
    const result = new Set();
    for (const value of target.values()) {
        result.add(cloneDeep(value));
    }
    return result;
}
/**
 * Function сreates deep copy of collection (Array, Object, Map, Set). Supports nested structures.
 * Works with primitive types (boolean, string, number, undefined, null)
 * and doesn't support (for now) Date, Buffer and the rest JS complex types.
 * Also doesn't copy class methods.
 *
 * For more details see unit tests.
 *
 * @example
 * ```ts
 * const obj = { prop1: 'string value', prop2: { prop3: [1, 2, 3] }};
 * const objCopy = cloneDeep(target);
 * console.log(obj !== objCopy); // true
 * const list = [{a: 1, b: 2}, 3, 4];
 * const listCopy = cloneDeep(list);
 * console.log(list !== listCopy); // true
 * list.push(5);
 * console.log(list.length === listCopy.length); // false
 *
 * // Mutation
 * obj.prop2.prop3.push(4);
 * console.log(obj.prop2.prop3); // [1, 2, 3, 4]
 * console.log(objCopy.prop2.prop3); // [1, 2, 3]
 *
 * target.prop1 = 10;
 * console.log(obj.prop1); // 10
 * console.log(objCopy.prop1); // string value
 * ```
 */
function cloneDeep(target) {
    if (target == null || isPrimitive(target))
        return target;
    if (target instanceof Map)
        return cloneMap(target);
    if (target instanceof Set)
        return cloneSet(target);
    const output = Array.isArray(target) ? [] : Object.create({});
    for (const [key, value] of Object.entries(target)) {
        if (Array.isArray(value)) {
            output[key] = value.map(cloneDeep);
        }
        else if (typeof value === 'object') {
            output[key] = cloneDeep(value);
        }
        else if (isPrimitive(value)) {
            output[key] = value;
        }
    }
    return output;
}

/**
 * Returns the value passed into the function. Has many
 * use cases in functional programming. It can be used
 * with e.g. `rxjs.Observable.map`, `Array.map` and more.
 *
 * @example
 * identity('Hello World!');
 * // 'Hello World!'
 * identity(1);
 * // 1
 * identity();
 * // undefined
 *
 * @example <caption>Conditionally transform values:</caption>
 * names.map(beLoud ? toUppercase : identity);
 * // e.g. ['CAROL', 'TONY'] or ['Carol', 'Tony']
 *
 * @example <caption>Can be used for simple array filtering:</caption>
 * [true, false, true, false].filter(identity);
 * // [true, true]
 */
function identity(value) {
    return value;
}

function comparer(mapFn = identity) {
    // tslint:disable-next-line:no-unbound-method
    const compare = new Intl.Collator(undefined, { numeric: true }).compare;
    return (a, b) => compare(mapFn(a), mapFn(b));
}

/**
 * Returns a promise that will resolve after `duration` ms.
 * To reject the before it resolves, an `AbortSignal` from an
 * [`AbortController`](https://developer.mozilla.org/en-US/docs/Web/API/AbortController)
 * can be supplied.
 *
 * `AbortController` is a DOM API that's used to cancel fetch requests.
 *
 * @param duration The number of milliseconds before resolving the promise
 * @param abortSignal Rejects the promise when signaled (see `AbortController`)
 *
 * @example
 * await delay(100);
 *
 *
 * @example
 * const controller = new AbortController();
 * // use controller.abort() somewhere else
 * try {
 *  await delay(2000, controller.signal);
 * } catch {
 *  console.log('I was rejected!');
 * }
 */
function delay(duration, abortSignal) {
    return new Promise((resolve, reject) => {
        const timerId = setTimeout(resolve, duration);
        if (abortSignal) {
            abortSignal.addEventListener('abort', () => {
                clearTimeout(timerId);
                reject();
            }, { once: true });
        }
    });
}

/**
 * Will use `map.get(key)` if the map has a value for the `key`, otherwise it
 * will execute the `defaultFn` and insert and return it's value.
 *
 * @example
 * const map = new Map<string, number[]>();
 * // ...
 * const testNumbers = getOrCreate(map, 'test', () => []);
 * // Will return `[]`
 *
 * // The above line is equivalent to writing this code:
 * const testNumbers = map.has('test') ? map.get('test')! : (() => {
 *   const temp = [];
 *   map.set('test', temp);
 *   return temp;
 * })();
 */
function getOrCreate(map, key, defaultFn) {
    if (map.has(key))
        return map.get(key);
    const value = defaultFn();
    map.set(key, value);
    return value;
}

/**
 * Internal helper for creating tagged template functions and joining them into a single string.
 *
 * @example
 * function test(strings: TemplateStringsArray, ...values: unknown[]) {
 *   return mergeTemplate(strings, values, String);
 * }
 */
function mergeTemplate(strings, values, transformFn) {
    return strings[0] + values.map((v, i) => transformFn(v) + strings[i + 1]).join('');
}

/**
 * Helps write readable template strings in code, while removing
 * indentation of the joined string result.
 *
 * Uses the smallest indentation of non-whitespace-only lines as the base,
 * while keeping the relative indentation for the other lines. Newlines
 * will be kept, except for first/last lines if they should be empty.
 *
 * See unit tests for more complete examples
 *
 * @example
 * const text = deindent`
 *   Hello there!
 *     This is some random text,
 *   should you care about it.
 *
 *   ${ 'Best wishes' }
 *    - Some dude
 * `;
 * text;
 * // 'Hello there!\n' +
 * // '  This is some random text,\n' +
 * // 'should you care about it.\n' +
 * // '\n' +
 * // 'Best wishes\n' +
 * // ' - Some dude'
 */
function deindent(strings, ...values) {
    const merged = mergeTemplate(strings, values, String);
    const count = Array.from(merged.matchAll(/^([ \t]*)\S+/gm))
        .map(([, whitespace]) => whitespace.length)
        .reduce((a, b) => Math.min(a, b), Infinity);
    return merged
        .replace(new RegExp(`^[ \\t]{${count < Infinity ? count : 0}}`, 'gm'), '')
        .replace(/(^[ \t]*\n|\n[ \t]*$)/g, '');
}

const DEFAULT_OPTIONS = {
    ignoreArrayOrder: false
};
function lengthOf(value) {
    if (value instanceof Map || value instanceof Set)
        return value.size;
    return Object.keys(value).length;
}
/**
 * @example
 * ```ts
 * classNameOf({}) // Object
 * classNameOf([]) // Array
 *
 * // Legacy browsers:
 * classNameOf({}) // [object Object]
 * classNameOf([]) // [object Array]
 * ```
 */
function classNameOf(value) {
    try {
        return value.constructor.name;
    }
    catch (error) {
        // IE 11 and Edge 12 don't support "name" property
        return Object.prototype.toString.call(value);
    }
}
/**
 * @description
 * Compares two collections (Array, Object, Map, Set) by properties.
 * Supports nested structures. Doesn't work with internal JS types like Date, Buffer and etc.
 *
 * For more details see unit tests.
 *
 * @param { boolean } options.ignoreArrayOrder - ignore order for arrays if items are primitives. Default - false
 *
 * @example
 * ```ts
 * const o1 = { a: 'foo', b: 'bar', c: 'baz' };
 * const o2 = { a: 'bar', b: 'foo', c: 'baz' };
 * isEqual(o1, o2); // false
 *
 * const o1 = [{ a: 1}, { b: 2 }];
 * const o2 = [{ a: 1}, { b: 2 }];
 * isEqual(o1, o2); // true
 *
 * const o1 = ['foo', 'bar', 'baz'];
 * const o2 = ['bar', 'foo', 'baz'];
 * isEqual(o1, o2, { ignoreArrayOrder: true }) // true
 * ```
 */
// tslint:disable-next-line: cyclomatic-complexity
function isEqual(o1, o2, options = DEFAULT_OPTIONS) {
    if (o1 == null || o2 == null)
        return o1 === o2;
    if (isPrimitive(o1) && isPrimitive(o2))
        return o1 === o2;
    if (classNameOf(o1) !== classNameOf(o2))
        return false;
    if (lengthOf(o1) !== lengthOf(o2))
        return false;
    if (options.ignoreArrayOrder && Array.isArray(o1) && Array.isArray(o2)) {
        if (o1.every(isPrimitive) && o2.every(isPrimitive)) {
            return o1.every(item => o2.includes(item));
        }
    }
    if (o1 instanceof Map && o2 instanceof Map) {
        for (const key of o1.keys()) {
            if (isEqual(o1.get(key), o2.get(key)))
                continue;
            return false;
        }
    }
    else if (o1 instanceof Set && o2 instanceof Set) {
        return isEqual(Array.from(o1.values()), Array.from(o2.values()));
    }
    else {
        for (const key of Object.keys(o1)) {
            if (isEqual(o1[key], o2[key], options))
                continue;
            return false;
        }
    }
    return true;
}

/**
 * In some cases it's useful to have a default value
 * for a variable with a function that does nothing.
 * Instead of creating many empty functions, it can
 * be clearer to import the named "No Operation" (noop)
 * function.
 *
 * @example
 * // Empty callback to do nothing
 * function myHandler(callback = noop) {
 *   // do some work
 *   callback(values);
 * }
 *
 * @example
 * // Default value on property
 * class SomeComponent {
 *  handler = noop;
 *
 *  onChange() {
 *    // No need to worry about `undefined`!
 *    this.handler();
 *  }
 *
 *  registerHandler(fn: () => any) {
 *    this.handler = fn;
 *  }
 * }
 */
function noop() { }

/**
 * Only ever execute the supplied function only once. Subsequent calls
 * will return the value from the first invocation.
 *
 * Example usage could be initialization work that can be prompted by
 * different code paths.
 *
 * @example
 * let count = 0;
 * const incrementOnce = once(() => ++count);
 *
 * incrementOnce(); // 1
 * incrementOnce(); // 1
 * count === 1; // true
 */
function once(method) {
    let executed = false;
    let returnValue;
    return function onceInner(...args) {
        if (!executed) {
            executed = true;
            returnValue = method.apply(this, args);
        }
        return returnValue;
    };
}

/**
 * Creates a pending promise that can be resolved or rejected by
 * calling the respective `resolve` and `reject` methods. The promise
 * can be accessed as `.promise`, and the `.pending` property will
 * be `false` when the promise has settled.
 *
 * Can be used to track an asynchronous "task", that might be hard to
 * track completely within a `Promise` constructor.
 *
 * @example
 * class Task {
 *   task: PendingPromise<string>;
 *
 *   startTask() {
 *     this.task = pendingPromise();
 *     return this.task.promise;
 *   }
 *
 *   // later
 *   completeTask() {
 *     if (task.pending) {
 *       this.task.resolve('Done!');
 *     }
 *   }
 * }
 */
function pendingPromise() {
    let isPending = true;
    const result = {
        get pending() {
            return isPending;
        },
        get promise() {
            return promise;
        }
    };
    const promise = new Promise((res, rej) => {
        result.resolve = value => {
            isPending = false;
            res(value);
        };
        result.reject = error => {
            isPending = false;
            rej(error);
        };
    });
    return result;
}

/**
 * Promisifes a node-style callback function to a promise style function.
 * The last parameter for the wrapped function must be a callback, where
 * the first parameter is `error` (`null`/`undefined` if there is no error),
 * and the second is the result.
 *
 * @example
 * declare function readWithCb(path: string, callback: (err: Error | undefined | null, result: string) => void);
 * const read = promisify(readWithCb);
 * const result: string = await read('/foo/bar');
 */
function promisify(fn) {
    return (...args) => new Promise((res, rej) => {
        fn(...args, (err, result) => {
            if (err != null)
                return rej(err);
            res(result);
        });
    });
}

/**
 * Create an array with values in the range `0..size`. Helpful
 * with Functional Programming to avoid regular `for`/`while` loops.
 *
 * This method is efficient, and the array is packed, to avoid
 * performance issues with "Holey" arrays.
 * @see https://v8.dev/blog/elements-kinds
 *
 * @example
 * range(0);
 * // []
 * range(3);
 * // [0, 1, 2]
 * range(5);
 * // [0, 1, 2, 3, 4]
 *
 * @param size The size of the array
 * @returns An array with `number` values from `0` to `size`
 */
function range(size) {
    // A regular `for` loop was fastest, a few diffenent options tested on JSPerf:
    // - new Array(n).fill(0).map((_, i) => i)
    // - Array.from(...)
    // - [...new Array(n)]
    const arr = [];
    for (let i = 0; i < size; i += 1) {
        arr.push(i);
    }
    return arr;
}

/**
 * Tagged template function for creating RegExp with escaped
 * embedded expressions for generic input. Any characters that
 * have special meaning in regular expressions (e.g. `^`, `$`, `+`)
 * will be escaped inside the expressions. Characters in the string
 * literal will be treated as normal regular expression characters.
 *
 * Inspired by:
 * * http://lea.verou.me/2018/06/easy-dynamic-regular-expressions-with-tagged-template-literals-and-proxies/
 * * https://2ality.com/2017/07/re-template-tag.html
 *
 * @example <caption>Usage:</caption>
 * const currency = '$';
 * const regex = re`\d+${currency}`;
 * // '$' will be escaped to '\$' to avoid being interpreted as
 * // end of input, and be matched to a literal '$' in matched string
 * regex.test('Give me 1000$ now!');
 * // true
 *
 * @example <caption>Usage with flags:</caption>
 * // Global and case-insensitive
 * const regex = re`/foo/gi`;
 * 'FOO Foo bar foo'.match(regex);
 * // ['FOO', 'Foo', 'foo']
 */
function re(strings, ...values) {
    const unescapedRaw = strings.raw.map(s => s.replace(/\\`/g, '`'));
    const pattern = mergeTemplate(unescapedRaw, values, escapeRegExp);
    if (!pattern.startsWith('/')) {
        return new RegExp(pattern);
    }
    const lastSlash = pattern.lastIndexOf('/');
    if (lastSlash === 0)
        throw new Error('Missing matching `/` at the end of `re`');
    return new RegExp(pattern.slice(1, lastSlash), pattern.slice(lastSlash + 1));
}
function escapeRegExp(str) {
    return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
}

/**
 * For resolving that the value is of the provided string `enum` type.
 *
 * @param value the value to check
 * @param enumType the enum to check the value against
 *
 * @example
 *
 * enum Gender {
 *   Male = 'male',
 *   Female = 'female'
 * }
 * const result = resolveEnum('male', Gender);
 * // result is type `Gender | undefined`
 */
function resolveEnum(value, enumType) {
    return Object.values(enumType).find((kind) => kind === value);
}

function stringSort(array, options = {}) {
    const { numeric, mapFn = identity } = options;
    return Array.from(array)
        .sort((a, b) => mapFn(a).localeCompare(mapFn(b), undefined, { numeric }));
}

/**
 * Turns a `camelCased` or `PascalCased` string into `kebab-case`.
 * Useful for conversions of variable names to HTML attributes.
 * PascalCased names won't begin with '-'.
 *
 * @example
 * toKebabCase('thisIsSweet') // 'this-is-sweet'
 * toKebabCase('LetItGo') // 'let-it-go'
 * toKebabCase('XMLHttpRequest') // 'xml-http-request'
 *
 * @param value camel/Pascal-Cased string to transform
 * @returns kebab-cased version of the parameter
 */
function toKebabCase(value) {
    return value
        // Inserts dashes between lower-case and upper-case characters
        // e.g. HTMLInputElement => HTMLInput-Element
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        // Inserts dashes between groups of uppercase characters
        // e.g. HTMLInput-Element => HTML-Input-Element
        .replace(/([A-Z])([A-Z])(?=[a-z])/g, '$1-$2')
        // e.g. HTML-Input-Element => html-input-element
        .toLowerCase();
}

export { MouseButton, assert, assertDefined, assertElement, assertObject, assertOneOf, assertString, assertType, assertURL, assertUnreachable, cloneDeep, comparer, deindent, delay, getOrCreate, identity, isDefined, isEqual, noop, once, pendingPromise, promisify, range, re, resolveEnum, stringSort, toKebabCase };
//# sourceMappingURL=util.mjs.map
